import type { JSX } from 'react'

import { useTranslation } from '../i18n.js'

/**
 * Two versions of one record, side by side, with an explicit choice. `DESIGN-TOKENS.md` §16.5.
 *
 * > "sync conflict resolver showing **both versions side by side with an explicit choice**"
 *
 * **Retry on a version conflict was a button that could not work.** `BlockedWrites` offered Retry
 * and Discard for every refusal, and for a `VERSION_CONFLICT` the queued body still carries the
 * `expectedVersion` that already lost the race: pressing Retry re-sent the same stale number and
 * got the same 409, so the only working option a shopkeeper had was to throw their own change
 * away. Fifty call sites in the counter send an `expectedVersion`, so this is not one screen's
 * problem.
 *
 * **The comparison is only the fields that differ.** A record has twenty columns and a conflict is
 * usually about one of them; listing all twenty is a diff nobody reads. A field present on one
 * side and absent on the other is still a difference and is shown as such, because "they cleared
 * it" and "they did not touch it" are different events.
 *
 * **The choice is explicit and both sides are named.** "Keep mine" re-sends what this person
 * typed, against the version the record is at now. "Keep theirs" discards this write, which loses
 * what it carried and nothing else in the product remembers it, so the caller confirms.
 */

export interface ConflictResolverProps {
  /** What this write was going to set. The queued body. */
  readonly mine: unknown
  /** The record as it now stands, which the server attached to the 409. */
  readonly theirs: unknown
  readonly onKeepMine: () => void
  readonly onKeepTheirs: () => void
}

/** A value as a line of text. Objects and arrays are summarised rather than dumped. */
function show(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') return Array.isArray(value) ? `[${String(value.length)}]` : '{…}'
  return String(value)
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {}

/**
 * The fields where the two disagree.
 *
 * `version` and `expectedVersion` are left out: they always differ, that is what a conflict is,
 * and showing them is a row that says "this is a conflict" to somebody already reading a conflict.
 */
export function differences(
  mine: unknown,
  theirs: unknown,
): readonly { readonly field: string; readonly mine: string; readonly theirs: string }[] {
  const a = asRecord(mine)
  const b = asRecord(theirs)
  const skip = new Set(['version', 'expectedVersion', 'id'])
  const fields = [...new Set([...Object.keys(a), ...Object.keys(b)])].filter((k) => !skip.has(k))
  return fields
    .map((field) => ({ field, mine: show(a[field]), theirs: show(b[field]) }))
    .filter((row) => row.mine !== row.theirs)
    .sort((x, y) => (x.field < y.field ? -1 : 1))
}

export function ConflictResolver({
  mine,
  theirs,
  onKeepMine,
  onKeepTheirs,
}: ConflictResolverProps): JSX.Element {
  const { t } = useTranslation()
  const rows = differences(mine, theirs)

  return (
    <div className="lm-conflict">
      <p className="lm-conflict__means">{t('conflict.means')}</p>

      {rows.length === 0 ? (
        // Possible, and worth saying rather than showing an empty table: somebody saved the same
        // values a moment earlier, so there is nothing to choose between and keeping either is the
        // same record.
        <p className="lm-hint">{t('conflict.noDifference')}</p>
      ) : (
        <table className="lm-conflict__table">
          <thead>
            <tr>
              <th scope="col">{t('conflict.field')}</th>
              <th scope="col">{t('conflict.mine')}</th>
              <th scope="col">{t('conflict.theirs')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.field}>
                <th scope="row">{row.field}</th>
                {/* An empty cell is said in words. A blank reads as a rendering fault, and
                    "they cleared it" is a thing somebody has to decide about. */}
                <td>{row.mine === '' ? t('conflict.empty') : row.mine}</td>
                <td>{row.theirs === '' ? t('conflict.empty') : row.theirs}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="lm-conflict__actions">
        <button type="button" className="lm-button lm-button--primary" onClick={onKeepMine}>
          {t('conflict.keepMine')}
        </button>
        <button type="button" className="lm-button lm-button--danger" onClick={onKeepTheirs}>
          {t('conflict.keepTheirs')}
        </button>
      </div>
    </div>
  )
}
